import { Box, Button, useTheme, Typography, MenuItem } from "@mui/material";
import CTextField from "components/CTextField";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormReturn,
} from "react-hook-form";
import { RegisterFormInputs } from "./useRegisterForm";
import CNumberField from "components/CNumberField";
import CAddressAutocomplete from "components/CAddressAutocomplete";
import CSelect from "components/CSelect";
import { usStates } from "assets/States";

type FormCoborrowerProps = {
  control: Control<RegisterFormInputs, unknown>;
  disabledField: boolean;
  disabledFieldMarital: boolean;
  errors: FieldErrors<RegisterFormInputs>;
  deleteCoborrower: () => void;
  form: UseFormReturn<RegisterFormInputs, undefined, undefined>;
};

type DividerProps = {
  color: string;
};

const Divider: React.FC<DividerProps> = ({ color }) => (
  <div
    style={{
      borderBottom: `1px solid ${color}`,
      margin: "30px 0",
    }}
  />
);

const FormCoborrower: React.FC<FormCoborrowerProps> = ({
  control,
  disabledField,
  disabledFieldMarital,
  errors,
  deleteCoborrower,
  form,
}) => {
  const theme = useTheme();
  const coborrowerStreet = form.watch("coborrowerAddress.street_line");
  return (
    <>
      <Box display={"flex"} justifyContent="space-between" alignItems="center">
        <Typography
          data-cy="secondaryBorrower"
          mt={4}
          mb={5}
          variant="h1"
          lineHeight={1.2}
          color={theme.palette.text.primary}
          letterSpacing={-0.8}
          px={{ xs: 2, md: 0 }}
          fontSize={{ xs: "1.5rem", md: "2rem" }}
        >
          Secondary Borrower
        </Typography>
        <Button
          data-cy="deleteCoborrowerInviteLink"
          variant="text"
          sx={{
            textTransform: "none",
          }}
          onClick={deleteCoborrower}
        >
          Delete Co-Borrower
        </Button>
      </Box>
      <Box
        width={"100%"}
        display={"flex"}
        alignItems={"flex-start"}
        flexDirection={{ xs: "column", md: "row" }}
      >
        <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
          <Controller
            control={control}
            name="legalFirstNameCoborrower"
            render={({ field }) => (
              <CTextField
                label="Legal First Name"
                placeholder="John"
                fullWidth
                autoComplete="off"
                disabled={disabledField}
                sx={{ mb: 3 }}
                {...field}
                error={errors?.legalFirstNameCoborrower?.message}
              />
            )}
          />
        </Box>
        <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
          <Controller
            control={control}
            name="legalLastNameCoborrower"
            render={({ field }) => (
              <CTextField
                label="Legal Last Name"
                placeholder="Doe"
                fullWidth
                autoComplete="off"
                disabled={disabledField}
                sx={{ mb: 3 }}
                {...field}
                error={errors?.legalLastNameCoborrower?.message}
              />
            )}
          />
        </Box>
      </Box>
      <Box
        width={"100%"}
        display={"flex"}
        alignItems={"flex-start"}
        flexDirection={{ xs: "column", md: "row" }}
      >
        <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
          <Controller
            control={control}
            name="legalMiddleNameCoborrower"
            render={({ field }) => (
              <CTextField
                label="Legal Middle Name (Optional)"
                fullWidth
                autoComplete="off"
                sx={{ mb: 3 }}
                {...field}
                error={errors?.legalMiddleNameCoborrower?.message}
              />
            )}
          />
        </Box>
        <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
          <Controller
            control={control}
            name="legalSuffixCoborrower"
            render={({ field }) => (
              <CTextField
                label="Suffix (Optional)"
                fullWidth
                autoComplete="off"
                disabled={disabledField}
                sx={{ mb: 3 }}
                {...field}
                error={errors?.legalSuffixCoborrower?.message}
              />
            )}
          />
        </Box>
      </Box>
      <Controller
        control={control}
        name="maritalStatusCoborrower"
        render={({ field }) => (
          <CTextField
            select
            fullWidth
            label="Marital Status"
            disabled={disabledFieldMarital}
            sx={{ mb: 3 }}
            {...field}
            error={errors?.maritalStatusCoborrower?.message}
            inputProps={{
              "data-cy": "b2InvMaritalStatus-select",
            }}
          >
            <MenuItem data-cy="b2Inv-Unmarried" value="Unmarried">
              Unmarried
            </MenuItem>
            <MenuItem data-cy="b2Inv-Married" value="Married">
              Married
            </MenuItem>
            <MenuItem data-cy="b2Inv-Separated" value="Separated">
              Separated
            </MenuItem>
          </CTextField>
        )}
      />
      <Controller
        control={control}
        name="phoneCoborrower"
        render={({ field }) => (
          <CNumberField
            fullWidth
            autoComplete="off"
            label="Cell Phone"
            disabled={disabledField}
            placeholder="+1(123)-123-1234"
            format={
              process.env.REACT_APP_ENV === "prod"
                ? "+1###############"
                : "+###############"
            }
            sx={{ mb: 3 }}
            allowEmptyFormatting
            {...field}
            error={errors?.phoneCoborrower?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="confirmEmailAddressCoborrower"
        render={({ field }) => (
          <CTextField
            label="Email Address"
            disabled={disabledField}
            placeholder="Type in here"
            fullWidth
            autoComplete="off"
            sx={{ mb: 3 }}
            {...field}
            error={errors?.confirmEmailAddressCoborrower?.message}
          />
        )}
      />
      <Controller
        name="coborrowerAddress"
        control={control}
        render={({ field }) => (
          <CAddressAutocomplete
            {...field}
            onChange={undefined}
            placeholder="Please enter the address of the house where the co-worrober currently live in."
            address={field.value}
            disabled={disabledField}
            setAddress={field.onChange}
            sx={{ mb: 3, mt: { xs: 2, md: 0 } }}
            label="Co-borrower home address"
            error={errors?.coborrowerAddress?.message}
          />
        )}
      />
      {coborrowerStreet === "My address is not listed" && (
        <>
          <Box
            width={"100%"}
            display={"flex"}
            alignItems={"flex-start"}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box width={{ xs: "100%", md: "50%" }}>
              <Controller
                control={control}
                name="manual_coborrower_street_number"
                render={({ field }) => (
                  <CTextField
                    label="Street Number"
                    placeholder="Street number"
                    fullWidth
                    autoComplete="off"
                    disabled={disabledField}
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.manual_coborrower_street_number?.message}
                  />
                )}
              />
            </Box>
            <Box width={{ xs: "100%", md: "50%" }}>
              <Controller
                control={control}
                name="manual_coborrower_street_name"
                render={({ field }) => (
                  <CTextField
                    label="Street Name"
                    placeholder="Street name"
                    fullWidth
                    autoComplete="off"
                    disabled={disabledField}
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.manual_coborrower_street_name?.message}
                  />
                )}
              />
            </Box>
            <Box width={{ xs: "100%", md: "50%" }}>
              <Controller
                control={control}
                name="manual_coborrower_street_suffix"
                render={({ field }) => (
                  <CTextField
                    label="Street Suffix"
                    placeholder="Ln, Rd, St..."
                    fullWidth
                    autoComplete="off"
                    disabled={disabledField}
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.manual_coborrower_street_suffix?.message}
                  />
                )}
              />
            </Box>
            <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
              <Controller
                control={control}
                name="manual_coborrower_apt"
                render={({ field }) => (
                  <CTextField
                    label="Apt/Suite"
                    placeholder="Unit #"
                    fullWidth
                    autoComplete="off"
                    disabled={disabledField}
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.manual_coborrower_apt?.message}
                  />
                )}
              />
            </Box>
          </Box>
          <Box
            width={"100%"}
            display={"flex"}
            alignItems={"flex-start"}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
              <Controller
                control={control}
                name="manual_coborrower_city"
                render={({ field }) => (
                  <CTextField
                    label="City"
                    placeholder="City"
                    fullWidth
                    autoComplete="off"
                    disabled={disabledField}
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.manual_coborrower_city?.message}
                  />
                )}
              />
            </Box>
            <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
              <Controller
                control={control}
                name="manual_coborrower_state"
                render={({ field }) => (
                  <CSelect
                    {...field}
                    helperError={errors?.manual_coborrower_state?.message}
                    label="State"
                    autoComplete="off"
                    disabled={disabledField}
                    options={usStates.map((state) => ({
                      label: state,
                      value: state,
                    }))}
                    {...form.register("manual_coborrower_state", {
                      required: "State is required",
                    })}
                  />
                )}
              />
            </Box>
            <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
              <Controller
                control={control}
                name="manual_coborrower_zipcode"
                render={({ field }) => (
                  <CTextField
                    label="Zipcode"
                    placeholder="Zipcode"
                    fullWidth
                    autoComplete="off"
                    disabled={disabledField}
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.manual_coborrower_zipcode?.message}
                  />
                )}
              />
            </Box>
          </Box>
        </>
      )}
      <Divider color={theme.palette.text.disabled} />
    </>
  );
};
export default FormCoborrower;
